import { Scalar } from "./type";

class DataQueryParams<FiltersT extends FilterParams = FilterParams> implements DataQueryParamsInterface<FiltersT> {
    public readonly pagination: PaginationParams;
    public readonly filters?: FiltersT;
    public readonly search?: string;

    constructor(params?: DataQueryParamsInterface<FiltersT>) {
        this.search = (params?.search || '').trim();
        this.search = this.search.length ? this.search : undefined;

        this.filters = params?.filters;
        this.filters = (this.filters && Object.keys(this.filters).length) ? this.filters : undefined;

        this.pagination = new PaginationParams(params?.pagination);
    }

    public withPagination = (pagination: PaginationParamsInterface | undefined): DataQueryParams<FiltersT> => {
        return new DataQueryParams({
            pagination,
            filters: this.filters,
            search: this.search
        });
    }

    public withPage = (page: number): DataQueryParams<FiltersT> => {
        return new DataQueryParams({
            pagination: this.pagination.withPage(page),
            filters: { ...this.filters! },
            search: this.search
        });
    }

    public withSize = (size: number): DataQueryParams<FiltersT> => {
        return new DataQueryParams({
            pagination: this.pagination.withSize(size),
            filters: { ...this.filters! },
            search: this.search
        });
    }

    public withSortBy = (sortBy: SortBy): DataQueryParams<FiltersT> => {
        return new DataQueryParams({
            pagination: this.pagination.withSort([sortBy]),
            filters: { ...this.filters! },
            search: this.search
        });
    }

    public withSearch = (search: string | undefined): DataQueryParams<FiltersT> => {
        return new DataQueryParams({
            ...this,
            search
        });
    }

    public withFilter = (name: keyof FiltersT, value: FilterValue): DataQueryParams<FiltersT> => {
        let filters = {
            ...this.filters!,
            [name]: value
        };

        return new DataQueryParams({
            pagination: this.pagination.clone(),
            filters,
            search: this.search
        });
    }

    public withNoFilters = (): DataQueryParams<FiltersT> => {
        return new DataQueryParams({
            pagination: this.pagination.clone(),
            filters: undefined!,
            search: this.search
        });
    }
}

export default DataQueryParams;

export interface DataQueryParamsInterface<FiltersT extends FilterParams = FilterParams> {
    readonly pagination?: PaginationParamsInterface;
    readonly filters?: FiltersT;
    readonly search?: string;
}

export class PaginationParams implements PaginationParamsInterface {
    readonly page: number = 1;
    readonly size: number = 50;
    readonly sort: Sort = [];

    public constructor(params?: Partial<PaginationParamsInterface>) {
        this.page = params?.page || this.page;
        this.size = params?.size || this.size;
        this.sort = params?.sort || this.sort;
    }

    public get startIndex(): number {
        if (!this.page || !this.size) {
            return 1;
        }

        return (this.page! - 1) * this.size! + 1;
    };

    public withSize = (size: number): PaginationParams => {
        return new PaginationParams({ ...this.all(), size });
    }

    public withPage = (page: number): PaginationParams => {
        return new PaginationParams({ ...this.all(), page });
    }

    public withSort = (sort: Sort): PaginationParams => {
        return new PaginationParams({ ...this.all(), sort });
    }

    public clone = (): PaginationParams => {
        return new PaginationParams({ ...this.all() });
    }

    private all = (): PaginationParamsInterface => {
        return {
            page: this.page,
            size: this.size,
            sort: this.sort,
        }
    }
}

export interface PaginationParamsInterface {
    readonly page: number;
    readonly size: number;
    readonly sort: Sort;
}

export interface PaginationParamsWithTotalInterface extends PaginationParamsInterface {
    readonly total: number;
}

export type Sort = Array<SortBy>;
export type SortBy = { field: string, dir?: SortDir | 'ASC' | 'DESC' };

export enum SortDir { ASC = 'ASC', DESC = 'DESC' }

export type FilterParams = { [field: string]: FilterValue };
export type FilterValue = Scalar | Array<Scalar> | undefined | object;